/**
 * Slice and re-join paragraph HTML by character offsets of its text content.
 *
 * Fragments of a paragraph that continues on the next page are cut here. The
 * cut is expressed as a character offset into the paragraph's textContent (the
 * same offsets the line measurement produces), and both sides are produced by
 * Range.cloneContents so inline formatting (bold, links, highlights) stays
 * well-formed on either side of the cut. Cutting at offset X and joining the
 * pieces again reproduces the original content.
 *
 * A forced break (<br>) sitting exactly at the cut belongs to the EARLIER
 * slice: the line it ends is on the previous page, and a continuation that
 * started with a <br> would draw an empty first line the pagination never
 * measured.
 */

const scratch = (): HTMLDivElement => document.createElement('div');

// DOM position for a text offset: the end of the text node that reaches it.
const positionAt = (root: HTMLElement, offset: number): { node: Node; offset: number } => {
  if (offset <= 0) return { node: root, offset: 0 };
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let acc = 0;
  let node = walker.nextNode();
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (acc + len >= offset) {
      return { node, offset: offset - acc };
    }
    acc += len;
    node = walker.nextNode();
  }
  return { node: root, offset: root.childNodes.length };
};

// Move a position that sits at the very end of a text node forward past any
// <br> elements that immediately follow it (also through the end of inline
// ancestors such as <b>), so the break stays with the earlier slice.
const afterFollowingBreaks = (root: HTMLElement, pos: { node: Node; offset: number }): { node: Node; offset: number } => {
  if (pos.node === root) return pos;
  if (pos.node.nodeType === Node.TEXT_NODE && pos.offset < (pos.node.textContent?.length ?? 0)) return pos;
  let node: Node = pos.node;
  let result = pos;
  // Climb while we are at the end of an inline ancestor
  while (node !== root) {
    let sibling = node.nextSibling;
    let moved = false;
    while (sibling && sibling.nodeType === Node.ELEMENT_NODE && (sibling as Element).tagName === 'BR') {
      const parent = sibling.parentNode as Node;
      const index = Array.prototype.indexOf.call(parent.childNodes, sibling);
      result = { node: parent, offset: index + 1 };
      node = sibling;
      sibling = sibling.nextSibling;
      moved = true;
    }
    if (sibling) return result; // something other than a break follows
    if (moved) node = result.node; // continue from the parent holding the breaks
    const parent = node.parentNode;
    if (!parent || parent === root) return result;
    node = parent;
  }
  return result;
};

export const htmlTextLength = (html: string): number => {
  const div = scratch();
  div.innerHTML = html;
  return div.textContent?.length ?? 0;
};

export const sliceHtml = (html: string, start: number, end?: number): string => {
  const div = scratch();
  div.innerHTML = html;
  const range = document.createRange();
  const s = afterFollowingBreaks(div, positionAt(div, start));
  range.setStart(s.node, s.offset);
  if (end === undefined) {
    range.setEnd(div, div.childNodes.length);
  } else {
    const e = afterFollowingBreaks(div, positionAt(div, end));
    range.setEnd(e.node, e.offset);
  }
  const out = scratch();
  out.appendChild(range.cloneContents());
  return out.innerHTML;
};

/** Canonical form of an HTML string (as the browser would serialise it). */
export const normaliseHtml = (html: string): string => {
  const div = scratch();
  div.innerHTML = html;
  return div.innerHTML;
};
